import { useState, useEffect, useCallback } from 'react'
import { Provider, observer, inject } from 'mobx-react'
import { Navigate, Route, Routes } from 'react-router'
import styled from '@emotion/styled'
import _ from 'lodash'
import { useTheme } from '@emotion/react'
import { css } from '@emotion/css'
import { API_URL } from '../../../api'
import { withRouterProps } from '../../../utils'
import NoGuide from './NoGuide'
import CompanyStock from './CompanyStock'
import AcceptRecommendation from './AcceptRecommendation'
import AcceptManagedAccount from './AcceptManagedAccount'
import ImplementChanges from './ImplementChanges'
import Congratulations from './Congratulations'
import ConfirmEmail from './ConfirmEmail'
import Download from './Download'
import SaveEmailDownloadGuide from './SaveEmailDownloadGuide'
import InsuringFinalExpenses from './static-pages/InsuringFinalExpenses'
import ReverseMortgage from './static-pages/ReverseMortgage'
import HandyChecklist from './static-pages/HandyChecklist'
import SpendDownOffboardingStore from './SpendDownOffboardingStore'
import { LogoHeader, Stepper, Page } from '../elements'
import { Loading } from '../../../components'
import GetYourGuide from './spend-down-retail/GetYourGuide'
import SaveModeCongratulationsRetail from './spend-down-retail/Congratulations'

function Dashboard(props) {
  const { location } = props
  const [state, setState] = useState('loading')
  const [spendDownOffboarding] = useState(() =>
    SpendDownOffboardingStore.create({}, { store: props.store })
  )
  const theme = useTheme()

  const initializeSpedDownOffboarding = useCallback(async () => {
    try {
      setState('loading')
      await Promise.all([
        props.store.getPerson(),
        props.store.getSpouse(),
        props.store.getDependents(),
        props.store.getContactInfo(),
        props.store.getDocuments(),
        props.store.getInstitutionalAccount(),
        props.store.getPlanOverview(),
      ])

      spendDownOffboarding.initializeSpedDownOffboarding()

      setState('done')
    } catch (err) {
      console.error(err)
      setState('error')
    }
  }, [props.store, spendDownOffboarding])

  useEffect(() => {
    const spendingAcceptedCase = _.get(props, 'store.spendingAcceptedCase', {})
    if (
      location.pathname !== '/spending/implement/insuring-final-expenses' &&
      location.pathname !== '/spending/implement/reverse-mortgage' &&
      location.pathname !== '/spending/implement/handy-checklist'
    ) {
      if (_.isEmpty(spendingAcceptedCase)) {
        if (_.get(props, 'store.config.spendingMode', null)) {
          return props.navigate('/spending/finalize-choices', { replace: true })
        }
      }
    }
    initializeSpedDownOffboarding()
    props.main.setSidebar(false)
    props.main.setTopNav(false)

    document.documentElement.className = css`
      background: url(${API_URL + theme.bgLeft}) no-repeat top right fixed;
      -webkit-background-size: cover;
      -moz-background-size: cover;
      -o-background-size: cover;
      background-size: cover;
    `

    return () => {
      props.main.setSidebar(true)
      props.main.setTopNav(true)
      document.documentElement.className = ''
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (state === 'loading') {
    return (
      <div
        css={css`
          margin: 20px;
        `}>
        <Loading />
      </div>
    )
  }

  if (state === 'error') {
    return <Page.ServerError>Oops! Something went wrong, please try again later</Page.ServerError>
  }

  const DetermineInitialPage = () => {
    return (
      <Navigate
        to={
          '/spending/implement/' +
          spendDownOffboarding.spendingRoutes[spendDownOffboarding.currentStep]
        }
        replace
      />
    )
  }

  return (
    <Provider spendDownOffboarding={spendDownOffboarding}>
      <Container>
        {location.pathname !== '/spending/implement/insuring-final-expenses' &&
          location.pathname !== '/spending/implement/reverse-mortgage' &&
          location.pathname !== '/spending/implement/handy-checklist' && (
            <>
              <LogoHeader />
              {spendDownOffboarding.enableSpendingStepper && (
                <Stepper
                  maxWidth='100%'
                  currentStep={spendDownOffboarding.activeSpendingStep(
                    props.location.pathname.slice(20)
                  )}
                  totalSteps={spendDownOffboarding.spendingRoutes.map(
                    (path, i) => 'Step ' + (i + 1)
                  )}
                />
              )}
            </>
          )}

        <Routes>
          <Route path='/no-guide' element={<NoGuide />} />
          <Route path='/company-stock' element={<CompanyStock />} />
          <Route path='/accept-recommendation' element={<AcceptRecommendation />} />
          <Route path='/accept-managed-account' element={<AcceptManagedAccount />} />
          <Route path='/implement-changes' element={<ImplementChanges />} />
          <Route path='/congratulations' element={<Congratulations />} />
          <Route path='/congratulations/2' element={<SaveModeCongratulationsRetail />} />
          <Route path='/confirm-email' element={<ConfirmEmail />} />
          <Route path='/get-your-guide' element={<GetYourGuide />} />
          <Route path='/download' element={<Download />} />
          <Route path='/save-email-download-guide' element={<SaveEmailDownloadGuide />} />
          <Route path='/insuring-final-expenses' element={<InsuringFinalExpenses />} />
          <Route path='/reverse-mortgage' element={<ReverseMortgage />} />
          <Route path='/handy-checklist' element={<HandyChecklist />} />
          <Route path='*' element={<DetermineInitialPage />} />
        </Routes>
      </Container>
    </Provider>
  )
}

export default withRouterProps(inject('store', 'main')(observer(Dashboard)))

const Container = styled.div`
  max-width: 1200px;
  padding: 8px;
  margin: auto;
`
